/**
 * Purchase Orders: the list, one document, and the four writes.
 *
 * Deliberately parallel to `useSalesOrders` rather than shared with it. The two
 * families touch the same tables, but they are separate document types with
 * separate query roots, and a shared hook would need a `side` parameter threaded
 * through every call site to accomplish nothing.
 *
 * The supplier name in a list row comes from `partySnapshot`, NOT from a join to
 * the live party row. That is why a supplier rename does not silently rewrite the
 * order list a month later: the list renders what the document says, which is the
 * same rule the printed page follows.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { resolveActiveBusiness } from '@data/services/businessSession';
import {
  loadDocument,
  listRecentDocuments,
  type RecentDocumentsPage,
} from '@data/services/documentReader';
import { loadBusinessIdentityWithImages } from '@data/services/businessReader';
import type { BusinessIdentity } from '@core/doc/renderA4';
import {
  createPurchaseOrderDraft,
  deletePurchaseOrderDraft,
  purchaseOrderFormOptions,
  updatePurchaseOrderDraft,
  type PurchaseOrderInput,
} from '@data/services/purchaseOrderService';
import { currentBusinessId, DOCUMENTS_QUERY_ROOT } from '@features/documents/useDocuments';

export const PURCHASE_ORDERS_QUERY_ROOT = ['purchase-orders'] as const;

/**
 * The query roots whose data changes when a PRODUCT or SUPPLIER is edited.
 *
 * These live in their own modules, so the write hooks here cannot import them
 * without a cycle — this constant is the shared name both sides import instead.
 * Without it, saving a new product price leaves an already-open Purchase Order
 * editor prefilling the OLD price, with nothing on screen to explain why.
 *
 * The invoice editor uses `['invoice-form-options']` rather than `['invoices',
 * 'options']` like every other document type, so the query key prefix differs.
 * `'invoices'` alone invalidated nothing — a new customer saved from the contacts
 * screen would never appear in the invoice customer selector until the query
 * went stale (after `staleTime`).
 */
export const MASTER_DATA_DEPENDENT_ROOTS = [
  ['purchase-orders'],
  ['sales-orders'],
  ['invoice-form-options'],
  ['quotations'],
] as const;

/** Suppliers and products for the editor's pickers. */
export function usePurchaseOrderOptions() {
  return useQuery({
    queryKey: [...PURCHASE_ORDERS_QUERY_ROOT, 'options'],
    queryFn: async () => {
      const id = await currentBusinessId();
      return id ? purchaseOrderFormOptions(getDb() as never, id) : { suppliers: [], products: [] };
    },
  });
}

export function usePurchaseOrders() {
  return useQuery({
    queryKey: [...PURCHASE_ORDERS_QUERY_ROOT, 'list'],
    queryFn: async (): Promise<RecentDocumentsPage> => {
      const id = await currentBusinessId();
      if (!id)
        return { documents: [], total: 0, truncated: false, partyNamesByDocumentId: new Map() };
      return listRecentDocuments(
        getDb() as never,
        id,
        200,
        true,
        'PURCHASE_ORDER',
      ) as Promise<RecentDocumentsPage>;
    },
  });
}

/** One purchase order, with its computed model, lines and the business letterhead. */
export function usePurchaseOrder(documentId: string | null) {
  return useQuery({
    queryKey: [...PURCHASE_ORDERS_QUERY_ROOT, 'document', documentId],
    enabled: documentId !== null,
    queryFn: async () => {
      if (!documentId) return null;
      const business = await resolveActiveBusiness(getDb() as never);
      const db = getDb();
      const stored = await db.documents.get(documentId);
      // The company boundary is checked HERE, not in the loader. `documentId` is a
      // global primary key, so without this a bookmark copied from another shop
      // would open that shop's supplier's order under this shop's letterhead.
      if (!stored || stored.docType !== 'PURCHASE_ORDER' || stored.businessId !== business.id) {
        throw new Error('The requested document is not a purchase order in the active business.');
      }
      const [model, businessIdentity, lines, children] = await Promise.all([
        loadDocument(db as never, documentId, {
          businessStateCode: business.stateCode,
          businessId: business.id,
        }),
        loadBusinessIdentityWithImages(db as never, business.id),
        db.documentLines.where('documentId').equals(documentId).toArray(),
        db.documentLinks.where('toDocumentId').equals(documentId).toArray(),
      ]);
      return {
        model,
        stored,
        business: businessIdentity as BusinessIdentity,
        lines,
        hasChildren: children.some((link) => link.fromDocumentId !== documentId),
      };
    },
  });
}

async function invalidatePurchaseOrders(client: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT }),
    client.invalidateQueries({ queryKey: PURCHASE_ORDERS_QUERY_ROOT }),
  ]);
}
export function useCreatePurchaseOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PurchaseOrderInput) => createPurchaseOrderDraft(getDb() as never, input),
    onSuccess: () => invalidatePurchaseOrders(client),
  });
}

export function useUpdatePurchaseOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Omit<PurchaseOrderInput, 'businessId'> }) =>
      updatePurchaseOrderDraft(getDb() as never, id, input),
    onSuccess: () => invalidatePurchaseOrders(client),
  });
}

export function useDeletePurchaseOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePurchaseOrderDraft(getDb() as never, id),
    onSuccess: () => invalidatePurchaseOrders(client),
  });
}
